
import { toast } from '@/components/ui/sonner';
import { Notification, NotificationData, createNotificationId } from '@/hooks/notifications/types';

// Глобальный массив для хранения всех уведомлений
// Используется как кеш между обновлениями состояния компонентов
let notificationsCache: Notification[] = [];

// Стандартное время отображения уведомлений - 5 секунд
const NOTIFICATION_DURATION = 5000;

// Ключ для localStorage, где будем хранить ID показанных уведомлений
const SHOWN_NOTIFICATIONS_KEY = 'shownNotifications';

// Период времени (в мс), в течение которого мы не показываем дубликаты уведомлений
const NOTIFICATION_DEDUP_TIME = 24 * 60 * 60 * 1000; // 24 часа

/**
 * Проверяет, было ли уже показано уведомление с таким ID
 */
const wasNotificationShown = (id: string): boolean => {
  try {
    const shownNotificationsStr = localStorage.getItem(SHOWN_NOTIFICATIONS_KEY);
    if (!shownNotificationsStr) return false;
    
    const shownNotifications = JSON.parse(shownNotificationsStr);
    
    // Проверяем, есть ли такой ID в списке показанных уведомлений
    return shownNotifications.some((item: {id: string, timestamp: number}) => 
      item.id === id && 
      (Date.now() - item.timestamp) < NOTIFICATION_DEDUP_TIME
    );
  } catch (e) {
    console.error('Ошибка при проверке показанных уведомлений:', e);
    return false;
  }
};

/**
 * Сохраняет ID показанного уведомления в localStorage
 */
const markNotificationAsShown = (id: string): void => {
  try {
    const shownNotificationsStr = localStorage.getItem(SHOWN_NOTIFICATIONS_KEY);
    let shownNotifications: {id: string, timestamp: number}[] = [];
    
    if (shownNotificationsStr) {
      shownNotifications = JSON.parse(shownNotificationsStr);
      
      // Удаляем старые записи (старше NOTIFICATION_DEDUP_TIME)
      shownNotifications = shownNotifications.filter(
        item => (Date.now() - item.timestamp) < NOTIFICATION_DEDUP_TIME
      );
    }
    
    // Добавляем новую запись
    shownNotifications.push({
      id,
      timestamp: Date.now()
    });
    
    // Сохраняем обновленный список
    localStorage.setItem(SHOWN_NOTIFICATIONS_KEY, JSON.stringify(shownNotifications));
  } catch (e) {
    console.error('Ошибка при сохранении ID показанного уведомления:', e);
  }
};

/**
 * Проверяет, существует ли уже уведомление с таким ID в кеше
 */
const notificationExists = (id: string): boolean => {
  return notificationsCache.some(notification => notification.id === id);
};

/**
 * Добавляет новое уведомление в систему
 * Отображает его как toast и сохраняет в истории
 * @param data Данные уведомления
 * @param showToast Показывать ли уведомление как toast, по умолчанию true
 */
export const addNotification = (data: NotificationData, showToast: boolean = true): Notification | null => {
  // Если ID не указан, генерируем его
  const notificationId = data.id || createNotificationId(data.type || 'system');
  
  // Проверяем, существует ли уже уведомление с таким ID в кеше
  if (notificationExists(notificationId)) {
    console.log(`Уведомление с ID ${notificationId} уже существует, пропускаем...`);
    return null;
  }
  
  // Для уведомлений о платежах и предупреждений проверяем, было ли оно уже показано
  if ((data.type === 'payment' || data.type === 'warning') && wasNotificationShown(notificationId)) {
    console.log(`Уведомление ${notificationId} уже было показано ранее, пропускаем...`);
    return null;
  }
  
  // Создаем объект уведомления
  const notification: Notification = {
    id: notificationId,
    title: data.title,
    message: data.message,
    timestamp: new Date().toISOString(),
    read: false,
    type: data.type || 'system'
  };
  
  // Сохраняем в глобальном кеше
  notificationsCache = [notification, ...notificationsCache];
  
  // Отображаем уведомление как toast, если требуется
  if (showToast) {
    showToastNotification(notification);
    // Помечаем уведомление как показанное
    markNotificationAsShown(notificationId);
  }
  
  return notification;
};

/**
 * Отображает уведомление в виде toast
 */
export const showToastNotification = (notification: Notification) => {
  const toastType = mapNotificationTypeToToastType(notification.type);
  
  toast[toastType](notification.title, {
    description: notification.message,
    duration: NOTIFICATION_DURATION,
  });
};

/**
 * Преобразует тип уведомления в тип toast
 */
const mapNotificationTypeToToastType = (type: string): 'info' | 'warning' | 'success' | 'error' => {
  switch (type) {
    case 'warning':
      return 'warning';
    case 'payment':
      return 'success';
    case 'error':  // Добавляем случай для обработки типа 'error'
      return 'error';
    case 'system':
    case 'rental':
    case 'subscription':
    default:
      return 'info';
  }
};

/**
 * Возвращает все сохраненные уведомления
 */
export const getNotifications = (): Notification[] => {
  return [...notificationsCache];
};

/**
 * Отмечает уведомление как прочитанное
 */
export const markNotificationAsRead = (id: string): Notification[] => {
  notificationsCache = notificationsCache.map(notification => 
    notification.id === id ? { ...notification, read: true } : notification
  );
  
  return getNotifications();
};

/**
 * Показать все непрочитанные уведомления как toast
 */
export const showAllNotifications = () => {
  const unreadNotifications = notificationsCache.filter(notification => !notification.read);
  unreadNotifications.forEach(showToastNotification);
};

/**
 * Очищает все уведомления
 */
export const clearAllNotifications = () => {
  notificationsCache = [];
  return [];
};
